import {Injectable} from '@angular/core';
import {DataPersistence, withCorrelationIdOf} from '@kion/ngrx-helper';
import * as OrderHistoryActions from './orderhistory.actions';
import {map, mergeMap, switchMap, take} from 'rxjs/operators';
import {OrderHistoryPartialState} from './orderhistory.reducer';
import {Actions, createEffect} from '@ngrx/effects';
import { OrderHistory, OrderHistorySearch} from '@kion/data-models';
import {ActionCache} from '../../../../../helper/ngrx-helper/src/lib/action-cache';
import {OrderHistoryQuery} from './orderhistory.selectors';
import {combineLatest, of} from 'rxjs';
import {OrderHistoryConnectors} from "./orderhistory.connectors";

@Injectable()
export class OrderHistoryEffects {

  loadOrderHistoryById$ = createEffect(() =>
      this.dataPersistence.fetch(
         OrderHistoryActions.loadOrderHistoryById,
         {
            run: (
               action: ReturnType<typeof OrderHistoryActions.loadOrderHistoryById>
            ) => {
               const state$ = this.dataPersistence.store.select(OrderHistoryQuery.getAllOrderHistoryById);
               return state$.pipe(
                  take(1),
                  switchMap(
                     (orderHistory) => {
                        return this.actionCache.withCache(
                           action,
                           () => this.backend.loadById(action.orderNumber).pipe(
                              map(
                                 (response: OrderHistory) => withCorrelationIdOf(
                                    action,
                                    OrderHistoryActions.loadOrderHistoryByIdSuccess({orderHistory: response})
                                 )
                              )
                           ),
                           () => of(withCorrelationIdOf(action,
                              OrderHistoryActions.loadOrderHistoryByIdSuccess({orderHistory: orderHistory[action.orderNumber]})))
                        )
                     }
                  )
               )
            },
            onError: (
               action: ReturnType<typeof OrderHistoryActions.loadOrderHistoryById>,
               error
            ) => {
               return withCorrelationIdOf(
                  action,
                  OrderHistoryActions.loadOrderHistoryByIdError({error: error})
               )
            }
         }
      )
   )

  search$ = createEffect(() =>
      this.dataPersistence.fetch(
         OrderHistoryActions.search,
         {
            run: (
               action: ReturnType<typeof OrderHistoryActions.search>
            ) => {
               const searchOptions$ = combineLatest(
                  [
                     this.dataPersistence.store.select(OrderHistoryQuery.getPagination),
                     this.dataPersistence.store.select(OrderHistoryQuery.getQuery),
                  ]
               );
               return searchOptions$.pipe(
                  take(1),
                  switchMap(
                     ([pagination, stateQuery]) => {
                        let query;
                        if (action.useState) {
                           query = stateQuery
                        } else {
                           query = action.query;
                        }
                        return this.backend.search(
                           query, action.useDefaultPage ? 1 : pagination.page, pagination.perPage, action.myOrders, action.truckId, action.sort
                        ).pipe(
                           map(
                              (response: OrderHistorySearch) => withCorrelationIdOf(
                                 action,
                                 OrderHistoryActions.searchSuccess({searchResult: {...response, query}})
                              )
                           )
                        )
                     }
                  )
               )
            },
            onError: (
               action: ReturnType<typeof OrderHistoryActions.search>,
               error
            ) => {
               console.error("Error", error);
               return withCorrelationIdOf(
                  action,
                  OrderHistoryActions.searchError({error: error})
               );
            }
         }
      )
  );

  updateQuery$ = createEffect(() =>
        this.dataPersistence.fetch(
            OrderHistoryActions.updateQuery,
            {
                run: (
                    action: ReturnType<typeof OrderHistoryActions.updateQuery>
                ) => {
                    const query$ = this.dataPersistence.store.select(OrderHistoryQuery.getQuery);
                    return query$.pipe(
                        take(1),
                        mergeMap(
                            () => {
                                return [
                                    withCorrelationIdOf(
                                        action,
                                        OrderHistoryActions.updateQuerySuccess({query: action.query})
                                    )
                                ];
                            }
                        )
                    )
                },
                onError: (
                    action: ReturnType<typeof OrderHistoryActions.updateQuery>,
                    error
                ) => {
                    return withCorrelationIdOf(
                        action,
                        OrderHistoryActions.updateQueryError({error: error})
                    );
                }
            }
        )
  );

  updatePerPage$ = createEffect(() =>
      this.dataPersistence.fetch(OrderHistoryActions.updatePerPage, {
         run: (action: ReturnType<typeof OrderHistoryActions.updatePerPage>) => {
            const options$ = this.dataPersistence.store.select(
               OrderHistoryQuery.getPagination
            );
            return options$.pipe(
               take(1),
               map((pagination) => {
                  return withCorrelationIdOf(
                     action,
                     OrderHistoryActions.updatePerPageSuccess({
                        pagination: {
                           ...pagination,
                           perPage: action.perPage,
                           page: 1,
                        },
                     })
                  );
               })
            );
         },
         onError: (
            action: ReturnType<typeof OrderHistoryActions.updatePerPage>,
            error
         ) => {
            return withCorrelationIdOf(
               action,
               OrderHistoryActions.updatePerPageError({ error: error })
            );
         },
      })
  );

  updatePage$ = createEffect(() =>
      this.dataPersistence.fetch(OrderHistoryActions.updatePage, {
         run: (action: ReturnType<typeof OrderHistoryActions.updatePage>) => {
            const options$ = this.dataPersistence.store.select(
               OrderHistoryQuery.getPagination
            );
            return options$.pipe(
               take(1),
               map((pagination) => {
                  return withCorrelationIdOf(
                     action,
                     OrderHistoryActions.updatePageSuccess({
                        pagination: {
                           ...pagination,
                           page: action.page,
                        },
                     })
                  );
               })
            );
         },
         onError: (
            action: ReturnType<typeof OrderHistoryActions.updatePage>,
            error
         ) => {
            return withCorrelationIdOf(
               action,
               OrderHistoryActions.updatePageError({ error: error })
            );
         },
      })
  );

    update$ = createEffect(() =>
        this.dataPersistence.fetch(
            OrderHistoryActions.update,
            {
                run: (
                    action: ReturnType<typeof OrderHistoryActions.update>
                ) => {
                    return this.backend.update(
                        action.orderId,
                        action.note,
                        action.poNumber
                    ).pipe(
                        map(
                            (response) => withCorrelationIdOf(
                                action,
                                OrderHistoryActions.updateSuccess({orderHistory: response})

                )
                        )
                    )
                },
                onError: (
                    action: ReturnType<typeof OrderHistoryActions.update>,
                    error
                ) => {
                    console.error("Error", error);
                    return withCorrelationIdOf(
                        action,
                        OrderHistoryActions.updateError({error: error})
                    )
                }
            }
        )
    );

  constructor(
    private actions$: Actions,
    private dataPersistence: DataPersistence<OrderHistoryPartialState>,
    private backend: OrderHistoryConnectors,
    private actionCache: ActionCache
  ) {
  }
}

